/* eslint-disable @typescript-eslint/consistent-type-imports */
import React from 'react';

import type { ADFEntity } from '@atlaskit/adf-utils/types';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import type { SelectionExtensionPlugin } from '@atlaskit/editor-plugin-selection-extension/selection-extension-plugin-type';

import { useDialogs } from '@confluence/dialogs/entry-points/useDialogs.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';

import {
	ANALYTICS_PAGE_MODE,
	ANALYTICS_UI_SUBJECT_ID,
	type AnalyticsOrigin,
} from '../../constants/snippetAnalytics.ts';
import { CreateSnippetModal } from '../../modals/CreateSnippetModal.tsx';
import { useSnippetAnalytics } from '../analytics/useSnippetAnalytics.ts';

export type UseHandleCreateSnippetProps = {
	contentId: string;
	spaceId: string;
	spaceKey: string;
	editorAPIRef?: React.RefObject<PublicPluginAPI<[SelectionExtensionPlugin]> | null | undefined>;
};

/**
 * Hook that returns a handler for creating a snippet from the current editor selection.
 * Pass editorAPIRef from the caller (e.g. a ref holding the fullPageEditorPresetAPI).
 *
 * @returns A handler that accepts an `origin` (e.g. CONTEXTUAL_TOOLBAR, BLOCK_MENU)
 *          and an optional pre-captured ADF document. Without ADF, it reads the current selection.
 */
export const useHandleCreateSnippet = ({
	contentId,
	spaceId,
	spaceKey,
	editorAPIRef,
}: UseHandleCreateSnippetProps): ((origin: AnalyticsOrigin, adf?: ADFEntity) => void) => {
	const { fireButtonClickedAnalytic } = useSnippetAnalytics();
	const { showModal } = useDialogs();
	const isLivePage = useIsCurrentPageLive();

	return (origin: AnalyticsOrigin, adf?: ADFEntity) => {
		const editorAPI = editorAPIRef?.current;
		if (!editorAPI) {
			return;
		}

		const docAdf =
			adf && expValEquals('platform_editor_snippets_plugin_confluence', 'isEnabled', true)
				? adf
				: editorAPI.selectionExtension?.actions.getDocumentFromSelection?.()?.selectedNodeAdf;

		// getDocumentFromSelection will always return adf of type 'doc',
		// so strip this doc wrapper for the saved content
		const adfContent = docAdf?.content?.filter((content: unknown) => content !== undefined);
		if (!adfContent || adfContent.length === 0) {
			return;
		}

		fireButtonClickedAnalytic(ANALYTICS_UI_SUBJECT_ID.CREATE_SNIPPET, {
			origin,
			pageMode: isLivePage ? ANALYTICS_PAGE_MODE.LIVE : ANALYTICS_PAGE_MODE.EDIT,
			selectionLength: JSON.stringify(adfContent)?.length,
		});

		showModal(CreateSnippetModal, {
			adf: docAdf,
			contentId,
			spaceId,
			spaceKey,
			editorAPI,
			analyticsOrigin: origin,
		});
	};
};
